import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, IconButton, Tooltip, CircularProgress,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import { IInhouseEntry, IInhouseWeekGroup, IInhouseCategory } from './types';
import { fetchEntries, deleteEntry, updateEntry, fetchCategories } from './inhouseApi';
import { hasPermission } from '../../utils/permissionUtils';
import InhouseStatsBar from './InhouseStatsBar';
import InhouseFilterBar from './InhouseFilterBar';
import InhouseWeekSection from './InhouseWeekSection';
import InhouseCategoryManager from './InhouseCategoryManager';

interface InhouseTimelineProps {
  onClose: () => void;
  onCreateNew: () => void;
  onEdit: (entry: IInhouseEntry) => void;
  currentUser: any;
  refreshKey: number;
}

// Build week groups from flat entry list
function groupEntriesByWeek(entries: IInhouseEntry[]): IInhouseWeekGroup[] {
  const groupMap = new Map<string, IInhouseWeekGroup>();

  // First add pinned entries as a special group
  const pinnedEntries = entries.filter(e => e.isPinned);
  const regularEntries = entries.filter(e => !e.isPinned);

  for (const entry of regularEntries) {
    const key = `${entry.year}-${entry.weekNumber}`;
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        weekNumber: entry.weekNumber,
        year: entry.year,
        label: `Vecka ${entry.weekNumber}, ${entry.year}`,
        entries: [],
        highlight: undefined,
      });
    }
    const group = groupMap.get(key)!;
    group.entries.push(entry);
    if (entry.isHighlight) {
      group.highlight = entry;
    }
  }

  const groups = Array.from(groupMap.values());
  // Sort weeks descending (newest first)
  groups.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.weekNumber - a.weekNumber;
  });

  // If there are pinned entries, prepend a special "pinned" group
  if (pinnedEntries.length > 0) {
    groups.unshift({
      weekNumber: 0,
      year: 0,
      label: 'Fästa inlägg',
      entries: pinnedEntries,
      highlight: pinnedEntries.find(e => e.isHighlight),
    });
  }

  return groups;
}

const LAST_VISIT_KEY = 'inhouse_last_visit';

const InhouseTimeline: React.FC<InhouseTimelineProps> = ({
  onClose,
  onCreateNew,
  onEdit,
  currentUser,
  refreshKey,
}) => {
  const [entries, setEntries] = useState<IInhouseEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [deleteDialogEntry, setDeleteDialogEntry] = useState<IInhouseEntry | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [lastVisitTimestamp, setLastVisitTimestamp] = useState<string | undefined>(undefined);
  const [categories, setCategories] = useState<IInhouseCategory[]>([]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [cardSize, setCardSize] = useState<'landscape' | 'square' | 'portrait'>('portrait');
  const [selectedWeek, setSelectedWeek] = useState<string>('all'); // 'all' or 'YYYY-WW'

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const LIMIT = 50;

  const isAdmin = currentUser && (
    hasPermission(currentUser, 'admin:all') ||
    hasPermission(currentUser, 'inhouse:create')
  );

  // Load categories
  useEffect(() => {
    fetchCategories().then(setCategories).catch(err => console.error('Failed to load categories:', err));
  }, [refreshKey]);

  // Track last visit for "NY" badge
  useEffect(() => {
    const lastVisit = localStorage.getItem(LAST_VISIT_KEY);
    if (lastVisit) {
      setLastVisitTimestamp(lastVisit);
    }
    // Update last visit timestamp to now
    localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
  }, []);

  // Search debounce
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchDebounced(searchQuery);
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  // Load entries
  const loadEntries = useCallback(async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setOffset(0);
      } else {
        setLoadingMore(true);
      }

      const currentOffset = reset ? 0 : offset;
      const data = await fetchEntries({
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        search: searchDebounced || undefined,
        limit: LIMIT,
        offset: currentOffset,
      });

      if (reset) {
        setEntries(data.entries);
      } else {
        setEntries(prev => [...prev, ...data.entries]);
      }
      setTotal(data.total);
      setOffset(currentOffset + data.entries.length);
    } catch (err) {
      console.error('Failed to load entries:', err);
      setSnackbar({ open: true, message: 'Kunde inte hämta inlägg', severity: 'error' });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedCategory, searchDebounced, offset]);

  // Reset and reload when filters change
  useEffect(() => {
    loadEntries(true);
  }, [selectedCategory, searchDebounced, refreshKey]);

  const hasMore = entries.length < total;

  // Handlers
  const handleDelete = async () => {
    if (!deleteDialogEntry) return;
    try {
      await deleteEntry(deleteDialogEntry._id);
      setEntries(prev => prev.filter(e => e._id !== deleteDialogEntry._id));
      setTotal(prev => prev - 1);
      setSnackbar({ open: true, message: 'Inlägget har tagits bort', severity: 'success' });
    } catch (err) {
      console.error('Failed to delete entry:', err);
      setSnackbar({ open: true, message: 'Kunde inte ta bort inlägget', severity: 'error' });
    } finally {
      setDeleteDialogEntry(null);
    }
  };

  const handleToggleHighlight = async (entry: IInhouseEntry) => {
    try {
      const updated = await updateEntry(entry._id, { isHighlight: !entry.isHighlight });
      setEntries(prev => prev.map(e => {
        if (e._id === updated._id) return updated;
        // If we set this entry as highlight, remove highlight from other entries in same week
        if (updated.isHighlight && e.weekNumber === updated.weekNumber && e.year === updated.year && e._id !== updated._id) {
          return { ...e, isHighlight: false };
        }
        return e;
      }));
    } catch (err) {
      console.error('Failed to toggle highlight:', err);
      setSnackbar({ open: true, message: 'Kunde inte uppdatera highlight', severity: 'error' });
    }
  };

  const handleTogglePin = async (entry: IInhouseEntry) => {
    try {
      const updated = await updateEntry(entry._id, { isPinned: !entry.isPinned });
      setEntries(prev => prev.map(e => e._id === updated._id ? updated : e));
    } catch (err) {
      console.error('Failed to toggle pin:', err);
      setSnackbar({ open: true, message: 'Kunde inte uppdatera nål', severity: 'error' });
    }
  };

  const allWeekGroups = groupEntriesByWeek(entries);

  // Build available weeks list for dropdown
  const availableWeeks = allWeekGroups
    .filter(g => g.weekNumber > 0) // exclude pinned pseudo-group
    .map(g => ({ value: `${g.year}-${g.weekNumber}`, label: `v${g.weekNumber}, ${g.year}` }));

  // Filter by selected week
  const weekGroups = selectedWeek === 'all'
    ? allWeekGroups
    : allWeekGroups.filter(g =>
        g.weekNumber === 0 || // always show pinned
        `${g.year}-${g.weekNumber}` === selectedWeek
      );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fafafa',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1, sm: 2 },
          bgcolor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CampaignIcon sx={{ color: '#072f53', fontSize: { xs: 22, sm: 28 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#072f53', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
            Inhouse
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5, display: { xs: 'none', sm: 'block' } }}>
            Se vad vi gör
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateNew}
              size="small"
              sx={{
                bgcolor: '#072f53',
                '&:hover': { bgcolor: '#0a4175' },
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              Lägg till
            </Button>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 1, sm: 3 },
          py: { xs: 1.5, sm: 3 },
        }}
      >
        {/* Stats bar - hidden for now, enable with showStats state if needed */}
        {/* <InhouseStatsBar refreshKey={refreshKey} /> */}

        {/* Filter bar */}
        <InhouseFilterBar
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          categories={categories}
          isAdmin={!!isAdmin}
          onManageCategories={() => setShowCategoryManager(true)}
          cardSize={cardSize}
          onCardSizeChange={setCardSize}
          selectedWeek={selectedWeek}
          onWeekChange={setSelectedWeek}
          availableWeeks={availableWeeks}
        />

        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Empty state */}
        {!loading && entries.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 10,
            }}
          >
            <CampaignIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchDebounced || selectedCategory !== 'all'
                ? 'Inga inlägg matchar din sökning'
                : 'Inga inlägg ännu'
              }
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchDebounced || selectedCategory !== 'all'
                ? 'Prova att ändra filter eller sökord'
                : 'Bli den första att lägga till ett inlägg!'
              }
            </Typography>
            {isAdmin && !searchDebounced && selectedCategory === 'all' && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateNew}
                sx={{
                  bgcolor: '#072f53',
                  '&:hover': { bgcolor: '#0a4175' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Lägg till första inlägget
              </Button>
            )}
          </Box>
        )}

        {/* Week sections */}
        {!loading && weekGroups.map(group => (
          <InhouseWeekSection
            key={`${group.year}-${group.weekNumber}`}
            weekGroup={group}
            isAdmin={isAdmin}
            currentUserId={currentUser?._id || ''}
            lastVisitTimestamp={lastVisitTimestamp}
            onEdit={onEdit}
            onDelete={(entry) => setDeleteDialogEntry(entry)}
            onToggleHighlight={handleToggleHighlight}
            onTogglePin={handleTogglePin}
            categories={categories}
            cardSize={cardSize}
          />
        ))}

        {/* Load more */}
        {!loading && hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Button
              variant="outlined"
              onClick={() => loadEntries(false)}
              disabled={loadingMore}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
              }}
            >
              {loadingMore ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              Ladda fler inlägg
            </Button>
          </Box>
        )}
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteDialogEntry} onClose={() => setDeleteDialogEntry(null)}>
        <DialogTitle>Ta bort inlägg?</DialogTitle>
        <DialogContent>
          <Typography>
            Är du säker på att du vill ta bort <strong>"{deleteDialogEntry?.title}"</strong>?
            Detta kan inte ångras.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogEntry(null)}>Avbryt</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Ta bort
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category manager dialog */}
      {showCategoryManager && (
        <InhouseCategoryManager
          open={showCategoryManager}
          onClose={() => setShowCategoryManager(false)}
          onCategoriesChanged={() => {
            fetchCategories().then(setCategories).catch(() => {});
          }}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InhouseTimeline;
